import React from 'react';
import { SearchInput, TextField, ChipField, Link, useRecordContext } from 'react-admin';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';

import List from '../../common/views/List';
import Datagrid from '../../common/Datagrid';
import ResourceLinkField from '../../common/fields/ResourceLinkField';
import RatingField from '../../common/fields/RatingField';
import DateField from '../../common/fields/DateField';
import { getChipColors } from '../../common/fields/ChipField';
import WorkloadField from '../../weeklies/fields/WorkloadField';

import { getActiveProjectsSeverity, getOccupancyRateSeverity, getWorkloadSeverity } from './teamStatusUtils';

const NOW = moment();

const rowSx = (record) => {
    let sx = {
        borderLeftColor: 'transparent',
        borderLeftWidth: 5,
        borderLeftStyle: 'solid',
    };

    const items = [
        getActiveProjectsSeverity(record?.active_projects_count) || 3,
        getWorkloadSeverity(record?.last_weekly?.workload) || 3,
        getOccupancyRateSeverity(record?.last_weekly?.occupancy_rate) || 3
    ];
    const severity = Math.ceil(items.reduce((acc, value) => acc + value) / items.length);

    switch (severity) {
        case 1:
            sx = { ...sx, borderLeftColor: theme => theme.palette.mode === 'dark' ? theme.palette.success.main : theme.palette.success.light };
            break;
        case 2:
            sx = { ...sx, borderLeftColor: theme => theme.palette.mode === 'dark' ? theme.palette.warning.main : theme.palette.warning.light };
            break;
        case 3:
            sx = { ...sx, borderLeftColor: theme => theme.palette.mode === 'dark' ? theme.palette.error.main : theme.palette.error.light };
            break;
        default:
            break;

    }
    return sx;
};

const userFilters = [
    <SearchInput source="q" alwaysOn />
];

const FullNameField = ({ record }) => (
    <Typography variant="body2">{record.firstname} {record.lastname}</Typography>
);

const ActiveProjectsField = () => {
    const record = useRecordContext();
    const { active_projects_count } = record;
    const severity = getActiveProjectsSeverity(active_projects_count);

    let sx = {};
    if (severity === 3) {
        sx = getChipColors('red');
    }
    else if (severity === 2) {
        sx = getChipColors('orange');
    }
    else if (severity === 1) {
        sx = getChipColors('green');
    }

    if (active_projects_count) {
        return (
            <Link to={`/projects?filter={"status":"active","user_id":${record.id}}`}>
                <ChipField record={record} source="active_projects_count" size="small" sx={sx} />
            </Link>
        );
    }

    return null;
};

const CustomRatingField = () => {
    const record = useRecordContext();
    const lastWeeklyDate = record?.last_weekly?.date;
    const disabled = NOW.diff(moment(lastWeeklyDate), 'days') >= 14;

    return (
        <Link to={`/weeklies/${record?.last_weekly?.id}/show`}>
            <Tooltip title={<DateField record={record} source="last_weekly.date" />}>
                <span>
                    <RatingField source="last_weekly.occupancy_rate" disabled={disabled} />
                </span>
            </Tooltip>
        </Link>
    );
};

const CustomWorkloadField = () => {
    const record = useRecordContext();
    const lastWeeklyDate = record?.last_weekly?.date;
    const disabled = NOW.diff(moment(lastWeeklyDate), 'days') >= 14;

    return (
        <Link to={`/weeklies/${record?.last_weekly?.id}/show`}>
            <Tooltip title={<DateField record={record} source="last_weekly.date" />}>
                <span>
                    <WorkloadField source="last_weekly.workload" disabled={disabled} />
                </span>
            </Tooltip>
        </Link>
    );
};

const TeamStatusList = (props) => (
    <List
        resource='users'
        sort={{ field: 'id', order: 'ASC' }}
        filter={{ active: true }}
        perPage={25}
        filters={userFilters}
        {...props}
    >
        <Datagrid rowSx={rowSx} bulkActionButtons={false}>
            <ResourceLinkField label="Name" resource="users">
                <FullNameField />
            </ResourceLinkField>
            <TextField source="trigram" label="" />
            <TextField source="status" />
            <ActiveProjectsField label="Active projects" />
            <CustomRatingField label="Occupancy rate" />
            <CustomWorkloadField label="Workload" />
        </Datagrid>
    </List>
);

export default TeamStatusList;